import { SimulateUseCaseInterface } from 'application/usecases/SimulateUseCase';
import DropdownIcon from 'assets/icons/simulate_dropdown.svg';
import DropupIcon from 'assets/icons/simulate_dropup.svg';
import { SimHistoryInfo, SimulateLoanEntity } from 'domain/entities/SimulateEntity';
import { openErrorNoti, openSuccessNoti } from 'presentation/components/Notification';
import PieChart from 'presentation/components/PieChart';
import {
    completeFetching,
    showConfirm,
    startFetching,
} from 'presentation/redux/features/app/appSlice';
import { useAppSelector } from 'presentation/redux/hooks';
import { AUTH_ROUTES } from 'presentation/router/CONSTANTS';
import React from 'react';
import { useCollapse } from 'react-collapsed';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import getMessage from 'utilities/constants/MESSAGE';
import { commonHelpers } from 'utilities/helpers';
import { SimHistoryModal } from '../../CU-010-02/components/SimHistoryModal';
import { SimInputTitleModal } from '../../CU-010-02/components/SimInputTitleModal';
import { MOCK } from '../models/mock';
import FormSimulateSale from './FormSimulateSale';
type props = {
    form: any;
    service: SimulateUseCaseInterface;
};

const Content = ({ form, service }: props) => {
    const location = useLocation();
    const { simId } = useParams();
    const [descData, setDescData] = React.useState<string>('');
    const [dataChart, setDataChart] = React.useState<any[]>(MOCK);
    const [amount, setAmount] = React.useState<string>('');
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { getCollapseProps, getToggleProps, isExpanded } = useCollapse();
    const userSlice = useAppSelector((state) => state.user);
    const [selectedHistorySimId, setSelectedHistorySimId] = React.useState<number>();
    const [historyList, setHistoryList] = React.useState<SimHistoryInfo[]>();
    const [openSimHistoryModal, setOpenSimHistoryModal] = React.useState<boolean>(false);
    const [openSimInputTitleModal, setOpenSimInputTitleModal] = React.useState<boolean>(false);
    const [btnDisable, setBtnDisable] = React.useState<boolean>(false);

    React.useEffect(() => {
        const payload = {
            ...location.state,
            simId,
        };
        getData(payload);
    }, []);

    const getData = async (value: any) => {
        try {
            dispatch(startFetching());
            const payload: SimulateLoanEntity = {
                annualIncome: Number(commonHelpers.toNumberOnly(value.annualIncome)),
                existingLoanAmount: Number(commonHelpers.toNumberOnly(value.existingLoanAmount)),
                simId: value?.simId ? Number(value?.simId) : null,
            };
            const response = await service.createTempSimulateLoan(payload);
            const data = response.result;
            setDescData(
                commonHelpers.formatNumberMoney(Math.round(data.loanPossibleAmount / 10000)),
            );
            setDataChart([
                {
                    id: 'ワンルームオークション内借入金', //A3
                    label: 'ワンルームオークション内借入金',
                    value: data.bbmLoanAmount,
                    color: '#183AAB',
                },
                {
                    id: '既存借入額', //A2
                    label: '既存借入額',
                    value: data.existingLoanAmount,
                    color: '#165469',
                },
                {
                    id: '融資可能額', //C1
                    label: '融資可能額',
                    value: data.loanPossibleAmount,
                    color: '#9AD1D3',
                },
            ]);

            setAmount(
                (data.loanLimitAmount!=null &&
                    commonHelpers.formatNumberMoney(data.loanLimitAmount) + '円') ||
                    '無制限',
            );

            // init form value
            form.setFieldsValue({
                annualIncome: commonHelpers.formatNumberMoney(data.annualIncome),
                existingLoanAmount: commonHelpers.formatNumberMoney(data.existingLoanAmount),
                title: data.simulationTitle,
            });
            // eslint-disable-next-line no-empty
        } catch (error: any) {
            openErrorNoti();
        } finally {
            dispatch(completeFetching());
        }
    };

    const checkLogin = async () => {
        if (userSlice?.isSignedIn) {
            const value = form.getFieldsValue();
            if (simId) {
                saveData(value.title);
            } else {
                openHistoryPopup(value);
            }
        } else {
            const confirmProps = {
                message: '以降のステップには\nログインが必要です。',
                okText: 'ログイン・アカウント作成へ',
                cancelText: 'いいえ',
                type: 'info',
                onOk: goToLoginPage,
            };
            dispatch(showConfirm(confirmProps));
        }
    };

    const openHistoryPopup = async (values: SimulateLoanEntity) => {
        try {
            dispatch(startFetching());
            const payload: SimulateLoanEntity = {
                ...values,
            };
            const responses = await Promise.all([
                service.getSimModalList('CU-052-02'),
                getData(payload),
            ]);
            const response = responses[0];
            if (response.result) {
                setHistoryList(response.result.listSim);
            }
            // eslint-disable-next-line no-empty
        } catch (err) {
        } finally {
            dispatch(completeFetching());
        }
    };

    const goToLoginPage = () => {
        navigate(`${AUTH_ROUTES.PATH}/${AUTH_ROUTES.SUB.CU_001_00}`);
    };

    React.useEffect(() => {
        if (historyList) {
            if (historyList.length >= 10) {
                setOpenSimHistoryModal(true);
            } else {
                openInputPopup();
            }
        }
    }, [historyList]);

    const openInputPopup = async (simId?: number) => {
        setSelectedHistorySimId(simId);
        setHistoryList(undefined);
        setOpenSimHistoryModal(false);
        setOpenSimInputTitleModal(true);
    };

    const saveData = async (title: string) => {
        try {
            setOpenSimInputTitleModal(false);
            const value = form.getFieldsValue();
            dispatch(startFetching());
            const payload: SimulateLoanEntity = {
                annualIncome: Number(commonHelpers.toNumberOnly(value.annualIncome)),
                existingLoanAmount: Number(commonHelpers.toNumberOnly(value.existingLoanAmount)),
                simId: simId
                    ? Number(simId)
                    : selectedHistorySimId
                      ? Number(selectedHistorySimId)
                      : null,
                title: title,
                simulationTitle: title,
            };
            await service.createSimulateLoan(payload);
            setBtnDisable(true);
            openSuccessNoti({ description: getMessage('S338') });
            // eslint-disable-next-line no-empty
        } catch (error: any) {
        } finally {
            dispatch(completeFetching());
        }
    };

    return (
        <div className="content" id="top-page">
            <div className="simulate-piechart">
                <PieChart data={dataChart} amount={amount} />
            </div>
            <div className="content-description">
                <div className="content-description__text">融資可能額</div>
                <div className="content-description__amount">
                    <span>{descData}</span>&nbsp;万円
                </div>
            </div>
            <div className="content-collapse" style={{ marginBottom: 30 }}>
                <div className="content-collapse__title">入力条件</div>
                <div className="content-collapse__icon" {...getToggleProps()}>
                    <img src={isExpanded ? DropupIcon : DropdownIcon} alt="dropdown icon" />
                </div>
            </div>
            <FormSimulateSale
                form={form}
                getCollapseProps={getCollapseProps}
                getData={getData}
                saveData={checkLogin}
                btnDisable={btnDisable}
                setBtnDisable={setBtnDisable}
            />
            <SimHistoryModal
                data={historyList}
                open={openSimHistoryModal}
                onOk={openInputPopup}
                onCancel={() => {
                    setHistoryList(undefined);
                    setOpenSimHistoryModal(false);
                }}
            />
            <SimInputTitleModal
                open={openSimInputTitleModal}
                onOk={saveData}
                onCancel={() => {
                    setHistoryList(undefined);
                    setOpenSimInputTitleModal(false);
                }}
            />
        </div>
    );
};

export default Content;
